import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Product } from '../data/products';
import { Lang } from '../data/translations';
import { authApi, User, AuthResponse } from '../api/auth';
import { api } from '../api/client';

export type Page =
  | 'home'
  | 'catalogue'
  | 'product'
  | 'cart'
  | 'checkout'
  | 'quote-request'
  | 'reseller'
  | 'services'
  | 'about'
  | 'orders'
  | 'login'
  | 'register'
  | 'forgot-password'
  | 'reseller-dashboard'
  | 'reseller-catalogue'
  | 'reseller-orders'
  | 'reseller-quotes'
  | 'reseller-quote-detail'
  | 'reseller-account'
  | 'admin-dashboard'
  | 'admin-products'
  | 'admin-categories'
  | 'admin-users'
  | 'admin-reseller-applications'
  | 'admin-orders'
  | 'admin-quotes'
  | 'verify-email' | 'admin-contact';

export interface CartItem {
  product: Product;
  qty: number;
  selectedSize?: string;
  selectedColor?: string;
}

export interface Order {
  id: string;
  type: 'direct' | 'quote';
  items: CartItem[];
  date: string;
  status: string;
  form: Record<string, string>;
}

interface AppContextType {
  lang: Lang;
  setLang: (l: Lang) => void;
  page: Page;
  setPage: (p: Page) => void;
  selectedProductId: string | null;
  setSelectedProductId: (id: string | null) => void;
  selectedQuoteId: number | null;
  setSelectedQuoteId: (id: number | null) => void;
  cart: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (productId: string) => void;
  updateQty: (productId: string, qty: number) => void;
  clearCart: () => void;
  cartCount: number;
  favorites: string[];
  toggleFavorite: (id: string) => void;
  orders: Order[];
  addOrder: (order: Order) => void;
  catalogueFilter: string;
  setCatalogueFilter: (f: string) => void;
  currentUser: User | null;
  authToken: string | null;
  isLoading: boolean;
  apiError: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  clearError: () => void;
  redirectAfterLogin: Page;
  setRedirectAfterLogin: (p: Page) => void;
  storeAuthData: (response: AuthResponse) => void;
  verifyEmailAndLogin: (code: string) => Promise<AuthResponse>;
}

const AppContext = createContext<AppContextType | null>(null);

const LS_CART = 'lodiss_cart';
const LS_FAV = 'lodiss_favorites';
const LS_ORDERS = 'lodiss_orders';
const LS_AUTH_TOKEN = 'lodiss_auth_token';
const LS_REFRESH_TOKEN = 'lodiss_refresh_token';

function loadLS<T>(key: string, fallback: T): T {
  try {
    const s = localStorage.getItem(key);
    return s ? (JSON.parse(s) as T) : fallback;
  } catch {
    return fallback;
  }
}

declare global { interface Window { __appSetPage: { setPage: (p: Page) => void }; } }
export function AppProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLang] = useState<Lang>('fr');
  // Initialize page from URL path
  const getInitialPage = (): Page => {
    const path = window.location.pathname;
    
    // Handle dynamic routes
    if (path.startsWith('/product/') && path.split('/').length > 2) {
      return 'product';
    }
    
    const urlToPage: Record<string, Page> = {
      '/': 'home',
      '/products': 'catalogue',
      '/cart': 'cart',
      '/checkout': 'checkout',
      '/quote-request': 'quote-request',
      '/revendeurs': 'reseller',
      '/services': 'services',
      '/about': 'about',
      '/orders': 'orders',
      '/login': 'login',
      '/register': 'register',
      '/forgot-password': 'forgot-password',
      '/set-password': 'set-password',
      '/reset-password': 'reset-password',
      '/reseller/dashboard': 'reseller-dashboard',
      '/reseller/catalogue': 'reseller-catalogue',
      '/reseller/orders': 'reseller-orders',
      '/reseller/quotes': 'reseller-quotes',
      '/reseller/account': 'reseller-account',
      '/admin/dashboard': 'admin-dashboard',
      '/admin/products': 'admin-products',
      '/admin/categories': 'admin-categories',
      '/admin/users': 'admin-users',
      '/admin/reseller-applications': 'admin-reseller-applications',
      '/admin/orders': 'admin-orders',
      '/admin/quotes': 'admin-quotes',
      '/admin/contact': 'admin-contact',
    };
    return urlToPage[path] || 'home';
  };

  const [page, setPage] = useState<Page>(getInitialPage());
  window.__appSetPage = { setPage };
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [selectedQuoteId, setSelectedQuoteId] = useState<number | null>(null);
  const [cart, setCart] = useState<CartItem[]>(() => loadLS<CartItem[]>(LS_CART, []));
  const [favorites, setFavorites] = useState<string[]>(() => loadLS<string[]>(LS_FAV, []));
  const [orders, setOrders] = useState<Order[]>(() => loadLS<Order[]>(LS_ORDERS, []));
  const [catalogueFilter, setCatalogueFilter] = useState<string>('all');
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(() => loadLS<string | null>(LS_AUTH_TOKEN, null));
  const [refreshToken, setRefreshToken] = useState<string | null>(() => loadLS<string | null>(LS_REFRESH_TOKEN, null));
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [redirectAfterLogin, setRedirectAfterLogin] = useState<Page>('home');

  useEffect(() => { localStorage.setItem(LS_CART, JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem(LS_FAV, JSON.stringify(favorites)); }, [favorites]);
  useEffect(() => { localStorage.setItem(LS_ORDERS, JSON.stringify(orders)); }, [orders]);
  useEffect(() => {
    if (authToken) {
      localStorage.setItem(LS_AUTH_TOKEN, JSON.stringify(authToken));
    } else {
      localStorage.removeItem(LS_AUTH_TOKEN);
    }
  }, [authToken]);

  useEffect(() => {
    if (refreshToken) {
      localStorage.setItem(LS_REFRESH_TOKEN, JSON.stringify(refreshToken));
    } else {
      localStorage.removeItem(LS_REFRESH_TOKEN);
    }
  }, [refreshToken]);

  useEffect(() => {
    api.setToken(authToken);
    api.setRefreshToken(refreshToken);
  }, [authToken, refreshToken]);

  useEffect(() => {
    const handleAuthLogout = () => {
      setCurrentUser(null);
      setAuthToken(null);
      setRefreshToken(null);
      setPage('login');
    };

    window.addEventListener('auth:logout', handleAuthLogout);
    return () => window.removeEventListener('auth:logout', handleAuthLogout);
  }, []);

  useEffect(() => {
    api.setToken(authToken);
  }, [authToken]);

  useEffect(() => {
    if (authToken) {
      setIsLoading(true);
      authApi.getCurrentUser()
        .then(user => {
          setCurrentUser(user);
          setApiError(null);
        })
        .catch(() => {
          setCurrentUser(null);
          setAuthToken(null);
          setRefreshToken(null);
        })
        .finally(() => setIsLoading(false));
    }
  }, [authToken]);

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    setApiError(null);
    try {
      const response = await authApi.login({ email, password });

      setAuthToken(response.access_token);
      setRefreshToken(response.refresh_token || null);
      api.setToken(response.access_token);
      api.setRefreshToken(response.refresh_token || null);
      setCurrentUser(response.user);

      const role = response.user.role;

      const getRoleDefaultPage = (r: string): Page => {
        if (r === 'super_admin') return 'admin-dashboard';
        if (r === 'reseller') return 'reseller-dashboard';
        return 'catalogue';
      };

      const isPageAllowedForRole = (targetPage: Page, r: string): boolean => {
        const adminPages = ['admin-dashboard', 'admin-products', 'admin-categories', 'admin-users',
          'admin-reseller-applications', 'admin-orders', 'admin-quotes', 'admin-contact'];
        const resellerPages = ['reseller-dashboard', 'reseller-catalogue',
          'reseller-orders', 'reseller-quotes', 'reseller-account'];

        if (adminPages.includes(targetPage)) return r === 'super_admin';
        if (resellerPages.includes(targetPage)) return r === 'reseller' || r === 'super_admin';
        return true;
      };

      // Use server redirect_to if available, otherwise use role-based default
      let destination: Page;
      if (response.redirect_to) {
        // Convert server path to page key
        const pathToPage: Record<string, Page> = {
          '/admin/dashboard': 'admin-dashboard',
          '/admin/products': 'admin-products',
          '/admin/categories': 'admin-categories',
          '/admin/users': 'admin-users',
          '/admin/reseller-applications': 'admin-reseller-applications',
          '/admin/orders': 'admin-orders',
          '/admin/quotes': 'admin-quotes',
          '/admin/contact': 'admin-contact',
          '/reseller/dashboard': 'reseller-dashboard',
          '/reseller/catalogue': 'reseller-catalogue',
          '/reseller/orders': 'reseller-orders',
          '/reseller/quotes': 'reseller-quotes',
          '/reseller/account': 'reseller-account',
          '/products': 'catalogue',
          '/cart': 'cart',
        };
        destination = pathToPage[response.redirect_to] || getRoleDefaultPage(role);
      } else {
        destination = getRoleDefaultPage(role);
      }

      if (
        redirectAfterLogin &&
        redirectAfterLogin !== 'home' &&
        redirectAfterLogin !== 'login' &&
        isPageAllowedForRole(redirectAfterLogin, role)
      ) {
        destination = redirectAfterLogin;
      }

      setRedirectAfterLogin('home');
      setPage(destination);

    } catch (err: any) {
      setApiError(err.message || 'Login failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [redirectAfterLogin]);

  const verifyEmailAndLogin = useCallback(async (code: string) => {
    setIsLoading(true);
    setApiError(null);
    try {
      const response = await authApi.verifyEmail(code);
      setAuthToken(response.access_token);
      if (response.refresh_token) setRefreshToken(response.refresh_token);
      api.setToken(response.access_token);
      api.setRefreshToken(response.refresh_token || null);
      setCurrentUser(response.user);
      return response;
    } catch (err: any) {
      setApiError(err.message || 'Verification failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await api.post('/auth/logout/');
    } catch {
    } finally {
      setCurrentUser(null);
      setAuthToken(null);
      setRefreshToken(null);
      setRedirectAfterLogin('home');
      setPage('home');
      setIsLoading(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    if (!authToken) return;
    setIsLoading(true);
    try {
      const user = await authApi.getCurrentUser();
      setCurrentUser(user);
    } catch {
      setCurrentUser(null);
      setAuthToken(null);
      setRefreshToken(null);
    } finally {
      setIsLoading(false);
    }
  }, [authToken]);

  const clearError = useCallback(() => setApiError(null), []);

  const storeAuthData = useCallback((response: AuthResponse) => {
    setAuthToken(response.access_token);
    setRefreshToken(response.refresh_token || null);
    api.setToken(response.access_token);
    api.setRefreshToken(response.refresh_token || null);
    setCurrentUser(response.user);
  }, []);

  const addToCart = useCallback((item: CartItem) => {
    setCart(prev => {
      const exists = prev.find(i => i.product.id === item.product.id);
      if (exists) {
        return prev.map(i =>
          i.product.id === item.product.id ? { ...i, qty: i.qty + item.qty } : i
        );
      }
      return [...prev, item];
    });
  }, []);

  const removeFromCart = useCallback((productId: string) => {
    setCart(prev => prev.filter(i => i.product.id !== productId));
  }, []);

  const updateQty = useCallback((productId: string, qty: number) => {
    if (qty <= 0) {
      setCart(prev => prev.filter(i => i.product.id !== productId));
    } else {
      setCart(prev => prev.map(i => i.product.id === productId ? { ...i, qty } : i));
    }
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const toggleFavorite = useCallback((id: string) => {
    setFavorites(prev =>
      prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
    );
  }, []);

  const addOrder = useCallback((order: Order) => {
    setOrders(prev => [order, ...prev]);
  }, []);

  const cartCount = cart.reduce((sum, i) => sum + i.qty, 0);

  return (
    <AppContext.Provider value={{
      lang, setLang,
      page, setPage,
      selectedProductId, setSelectedProductId,
      selectedQuoteId, setSelectedQuoteId,
      cart, addToCart, removeFromCart, updateQty, clearCart,
      cartCount,
      favorites, toggleFavorite,
      orders, addOrder,
      catalogueFilter, setCatalogueFilter,
      currentUser, authToken, isLoading, apiError,
      login, logout, refreshUser, clearError,
      redirectAfterLogin, setRedirectAfterLogin,
      storeAuthData, verifyEmailAndLogin,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
